import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logoBlack from "@/assets/logo-black.svg";
import logoWhite from "@/assets/logo-white.svg";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    // Check initial scroll position
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      setMenuOpen(false);
      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled 
          ? "bg-[#F5ECD7]/95 backdrop-blur-sm border-b border-[#7A9E7E]/20" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.a
            href="/"
            data-testid="logo"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src={scrolled ? logoBlack : logoWhite}
              alt="alongside.one"
              className="h-10 md:h-12 w-auto transition-all duration-500"
            />
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-12">
            <button
              onClick={() => scrollToSection("why")}
              className={`font-sans text-sm transition-colors ${
                scrolled ? "text-[#2C2C2C]/70 hover:text-[#2C2C2C]" : "text-white/80 hover:text-white"
              }`}
            >
              Why
            </button>
            <button
              onClick={() => scrollToSection("who")}
              className={`font-sans text-sm transition-colors ${
                scrolled ? "text-[#2C2C2C]/70 hover:text-[#2C2C2C]" : "text-white/80 hover:text-white"
              }`}
            >
              Who
            </button>
            <button
              onClick={() => scrollToSection("pricing")}
              className={`font-sans text-sm transition-colors ${
                scrolled ? "text-[#2C2C2C]/70 hover:text-[#2C2C2C]" : "text-white/80 hover:text-white"
              }`}
            >
              Pricing
            </button>
          </nav>

          {/* CTA Button */}
          <motion.button
            data-testid="header-cta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onClick={() => scrollToSection("pricing")}
            className="hidden md:block font-serif italic text-sm px-6 py-3 bg-[#E8A838] text-[#2C2C2C] hover:bg-[#C17A54] hover:text-white transition-all duration-300 rounded-full"
          >
            Let's Talk
          </motion.button>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2"
            aria-label="Toggle menu"
          >
            <div className="w-6 flex flex-col gap-1.5">
              <span className={`block h-px transition-transform ${scrolled ? "bg-[#2C2C2C]" : "bg-white"} ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
              <span className={`block h-px transition-opacity ${scrolled ? "bg-[#2C2C2C]" : "bg-white"} ${menuOpen ? "opacity-0" : ""}`} />
              <span className={`block h-px transition-transform ${scrolled ? "bg-[#2C2C2C]" : "bg-white"} ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#F5ECD7] border-b border-[#7A9E7E]/20"
          >
            <nav className="flex flex-col px-6 py-6 gap-4">
              <button
                onClick={() => scrollToSection("why")}
                className="font-sans text-base text-[#2C2C2C] text-left py-2"
              >
                Why
              </button>
              <button
                onClick={() => scrollToSection("who")}
                className="font-sans text-base text-[#2C2C2C] text-left py-2"
              >
                Who
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="font-sans text-base text-[#2C2C2C] text-left py-2"
              >
                Pricing
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="font-serif italic text-base mt-4 px-6 py-3 bg-[#E8A838] text-[#2C2C2C] hover:bg-[#C17A54] transition-all rounded-full"
              >
                Let's Talk
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
